import { SOURCE_PAGE } from '@chegg-wt/core/constants/citationFlow'
import React, { useEffect } from 'react'
import { connect, useSelector } from 'react-redux'
import { AppState } from '@chegg-wt/core/state'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { MainWrapper } from '@chegg-wt/core/components/Layouts/styled'
import { BreadcrumbPart, Breadcrumbs } from '@chegg-wt/core/components/Breadcrumbs'

import BreadcrumbData from '@chegg-wt/core/components/Head/BreadcrumbData'
import sourceTypes from '@chegg-wt/core/constants/sourceTypes'
import Credibility from '@chegg-wt/core/components/Credibility'
import ErrorContainer from '@chegg-wt/core/components/ErrorContainer'
import Resources from '@chegg-wt/core/components/Resources'
import SeoContent from '@chegg-wt/core/components/SeoContent'
import SourceTypeLinkPicker from '@chegg-wt/core/components/SourceTypeLinkPicker'
import UpgradeAdFrame from '@chegg-wt/core/components/UpgradeAdFrame'
import { citationValidityVariationSelector } from '@chegg-wt/core/state/bibliography/selectors'
import {
  AppRoute,
  AppRouteName,
  AppRouteOpts,
  CitationValidityVariations,
  JournalType,
  SourceType,
} from '@chegg-wt/core/types/core'
import {
  journalEngineSelected,
  sourceQueryEntered,
  sourceTypeSelected,
} from '@chegg-wt/core/state/createCitation/actions'
import { fetchUrlStyleName } from '@chegg-wt/core/state/currentProject/actions'
import {
  currentStyleSelector,
  currentUrlStyleNameSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import { Config } from '@chegg-wt/core/config'
import CreateAccountBanner from '@chegg-wt/core/components/CreateAccountBanner'
import {
  createCitationQuerySelector,
  createCitationSourceTypeSelector,
  currentSourceSelector,
  searchWidgetExpEnabledSelector,
} from '@chegg-wt/core/state/createCitation/selectors'
import { Dispatch } from 'redux'
import { isInvalidQuery } from '@chegg-wt/core/hooks/useSearchSources'
import { JOURNAL } from '@chegg-wt/core/constants/journalTypes'
import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import { CROSSREF_RESULT_COUNT } from '@chegg-wt/core/state/optimizely/constants'
import CitationSearchBar from '@chegg-wt/core/components/CitationSearchBar/index'
import { useIsLaptopOrSmallerScreen } from '@chegg-wt/core/hooks/useScreenSize'
import { useRouter } from 'next/router'
import {
  origins,
  Region,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { QueryParams } from '../../SourceSearchPageStructure/types'
import CitationSearchInput from '../../CitationSearchInput/index'
import { defaultSourceType } from '../../../utils/sourceTypes'
import * as Styled from './styled'
import {
  SourceTypeSelectionDispatchProps,
  SourceTypeSelectionProps,
  SourceTypeSelectionStateProps,
} from './types'

const SourceTypeSelection: React.FC<SourceTypeSelectionProps> = ({
  styleId,
  urlStyleName,
  initialUrlStyleName,
  currentStyle: style,
  selectSourceType,
  urlStyleNameFetched,
  copy = {},
  currentSourceType,
  sourceSearchQuery,
  setSearchQuery,
  changeJournalType,
  navigated,
  onCreateManualCitationClickCTA,
  citationValidityVariation,
}) => {
  const { toSourceSearch, toManualCitation } = useRoutesContext()
  const routes = useRoutesContext()
  const dropdownSourceTypes = [...sourceTypes]
  const buttonSourceTypes = dropdownSourceTypes.splice(0, 3)

  const seoOrCredibilityResourcesContent = copy.seoContent ? (
    <SeoContent content={copy.seoContent} advertPlacement="C2" />
  ) : (
    <>
      {!!copy.considerCredibility && (
        <Credibility copy={copy.considerCredibility} advertPlacement="C2" />
      )}
      {!!copy.resources && <Resources copy={copy.resources} />}
    </>
  )

  const hasBreadcrumbs =
    typeof styleId === 'string' && typeof initialUrlStyleName === 'string'

  useEffect(() => {
    if (hasBreadcrumbs) urlStyleNameFetched(styleId)
  }, [styleId])

  const breadcrumbs: BreadcrumbPart[] = [
    {
      label: 'Home',
      route: routes.toCitationsHome(),
    },
    {
      label: `${urlStyleName || initialUrlStyleName} Citation Generator`,
      route: routes.toSourceTypeSelection({ style: styleId }),
      last: true,
    },
  ]

  const composeSourceTypeLink = (sourceType: SourceType): AppRoute => {
    const routeParams = { style, sourceType: sourceType.id }
    if (sourceType.manual) {
      return toManualCitation({ ...routeParams })
    }
    return toSourceSearch({ ...routeParams })
  }

  const isSearchWidgetExpEnabled = useSelector(searchWidgetExpEnabledSelector)
  const selectedSource = useSelector(currentSourceSelector) || defaultSourceType
  useEffect(() => {
    if (isSearchWidgetExpEnabled) {
      selectSourceType(defaultSourceType.id)
    }
  }, [isSearchWidgetExpEnabled])

  const { isEnabled: crossrefResultCountEnabled } = useSafeOptimizelyFeature(
    CROSSREF_RESULT_COUNT
  )

  const onChangeJournalType = (type: JournalType) => {
    changeJournalType(type, crossrefResultCountEnabled)
  }

  const handleSearchQuery = (searchQuery: string) => {
    if (!isInvalidQuery(currentSourceType, searchQuery)) onChangeJournalType(JOURNAL)
    if (searchQuery !== sourceSearchQuery) {
      setSearchQuery(searchQuery)
    }
  }

  const onSourceTypeSelect = ({ id: sourceType }: SourceType) => {
    selectSourceType(sourceType, 'toSourceSearch')
    if (sourceSearchQuery) {
      handleSearchQuery(sourceSearchQuery)
    }
  }

  const handleManualCitationClick = (region: Region) => {
    navigated(routes.toManualCitation({ style, sourceType: selectedSource.value }), {
      replace: true,
    })
    onCreateManualCitationClickCTA(region)
  }

  const isLaptopOrSmaller = useIsLaptopOrSmallerScreen()
  const router = useRouter()
  const { searchQuery: initialQuery = '' } = router.query as QueryParams

  return (
    <>
      {hasBreadcrumbs ? <BreadcrumbData breadcrumbs={breadcrumbs} /> : null}
      <Styled.PageContainer>
        <UpgradeAdFrame copy={copy?.upgradeCopy} page={SOURCE_PAGE}>
          <ErrorContainer />
          <MainWrapper>
            {isLaptopOrSmaller && isSearchWidgetExpEnabled ? (
              <CitationSearchInput
                composeRoute={composeSourceTypeLink}
                sourceType={selectedSource}
                initialSearchQuery={initialQuery}
                onChangeSourceType={onSourceTypeSelect}
                onChangeSearchQuery={setSearchQuery}
                onManualCiteClick={handleManualCitationClick}
              />
            ) : (
              <>
                <Styled.Box>
                  <SourceTypeLinkPicker
                    buttonSourceTypes={buttonSourceTypes}
                    dropdownSourceTypes={dropdownSourceTypes}
                    composeRoute={composeSourceTypeLink}
                    onSourceTypeSelect={onSourceTypeSelect}
                    selectedSourceId={selectedSource.id}
                  />
                </Styled.Box>
                {isSearchWidgetExpEnabled && <CitationSearchBar />}
              </>
            )}
          </MainWrapper>
          {!Config.flankerSitesBannerCTAUpdate &&
            citationValidityVariation === CitationValidityVariations.CONTROL && (
              <CreateAccountBanner isSourceSelectionPage />
            )}
        </UpgradeAdFrame>
        {seoOrCredibilityResourcesContent}
        {hasBreadcrumbs ? <Breadcrumbs parts={breadcrumbs} divider=">" /> : null}
      </Styled.PageContainer>
    </>
  )
}

const mapStateToProps = (state: AppState) => ({
  currentStyle: currentStyleSelector(state),
  urlStyleName: currentUrlStyleNameSelector(state),
  currentSourceType: createCitationSourceTypeSelector(state),
  sourceSearchQuery: createCitationQuerySelector(state),
  citationValidityVariation: citationValidityVariationSelector(state),
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  selectSourceType: (sourceType: string, redirectTo?: AppRouteName) =>
    dispatch(sourceTypeSelected(sourceType, redirectTo)),
  urlStyleNameFetched: fetchUrlStyleName,
  setSearchQuery: (query: string) => dispatch(sourceQueryEntered(query)),
  changeJournalType: (type: JournalType, crossrefResultCountEnabled: boolean) =>
    dispatch(journalEngineSelected(type, crossrefResultCountEnabled)),
  navigated: (route: AppRoute, opts?: AppRouteOpts) => dispatch(navigated(route, opts)),
  onCreateManualCitationClickCTA: (region: Region) =>
    dispatch(userClick({ origin: origins.createManualCitationCTA, region })),
})

export default connect<SourceTypeSelectionStateProps, SourceTypeSelectionDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(SourceTypeSelection)
